/** Emit-able levels in ascending severity (excludes `silent`). */
export const LEVELS = [
    'silly',
    'trace',
    'debug',
    'info',
    'warn',
    'error',
    'fatal',
];
const LEVEL_VALUE = {
    silly: 0,
    trace: 1,
    debug: 2,
    info: 3,
    warn: 4,
    error: 5,
    fatal: 6,
    silent: 100,
};
/**
 * OTLP `severityNumber` per the OpenTelemetry logs data model (TRACE 1–4, DEBUG 5–8, INFO 9–12,
 * WARN 13–16, ERROR 17–20, FATAL 21–24). SigNoz buckets/colours logs by this number.
 */
const OTLP_SEVERITY = {
    silly: 1,
    trace: 4,
    debug: 5,
    info: 9,
    warn: 13,
    error: 17,
    fatal: 21,
};
const STORAGE_KEY = 'talview:log-level';
export function levelValue(level) {
    return LEVEL_VALUE[level];
}
export function otlpSeverity(level) {
    return OTLP_SEVERITY[level];
}
/** True when a record at `level` should pass a sink whose floor is `min`. */
export function isLevelEnabled(level, min) {
    return LEVEL_VALUE[level] >= LEVEL_VALUE[min];
}
export function isLogLevel(value) {
    return typeof value === 'string' && value in LEVEL_VALUE;
}
/** `warn` in production, `debug` otherwise — keyed off the (Rspack-inlined) `NODE_ENV`. */
export function resolveDefaultLevel(nodeEnv) {
    const env = nodeEnv ?? (typeof process !== 'undefined' && process.env ? process.env['NODE_ENV'] : undefined);
    return env === 'production' ? 'warn' : 'debug';
}
/**
 * Support/QA override: `localStorage['talview:log-level'] = 'trace'` raises verbosity for one
 * session without a redeploy. Returns `undefined` when unset or unavailable (SSR/Node).
 */
export function readStoredLevel() {
    try {
        if (typeof localStorage === 'undefined')
            return undefined;
        const raw = localStorage.getItem(STORAGE_KEY);
        return isLogLevel(raw) ? raw : undefined;
    }
    catch {
        return undefined;
    }
}
export function writeStoredLevel(level) {
    try {
        if (typeof localStorage === 'undefined')
            return;
        localStorage.setItem(STORAGE_KEY, level);
    }
    catch {
        // no-op: private mode / disabled storage must never break logging
    }
}
/** The effective starting level: stored override wins, else explicit config, else env default. */
export function resolveInitialLevel(configured, nodeEnv) {
    return readStoredLevel() ?? configured ?? resolveDefaultLevel(nodeEnv);
}
